import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import toast from 'react-hot-toast';
import api from '../../lib/request';
import config from '../../config';

const COMMENT_MAX_LENGTH = 2000;

const normalizeScope = (scope) => (scope === 'step' ? 'step' : 'guide');

const capitalizeFirstLetter = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/^(\s*\S)/, (match) => match.toUpperCase());
};

const buildCommentTree = (comments, scope, stepPosition, stepId) => {
  const filtered = (comments || []).filter((comment) => {
    if (normalizeScope(comment.scope) !== scope) return false;
    if (scope === 'step') {
      if (stepId) {
        return comment.stepId === stepId || (!comment.stepId && Number(comment.stepPosition) === Number(stepPosition));
      }
      return Number(comment.stepPosition) === Number(stepPosition);
    }
    return true;
  });

  const nodes = new Map();
  filtered.forEach((comment) => {
    nodes.set(comment.id, { ...comment, replies: [] });
  });

  const roots = [];
  filtered.forEach((comment) => {
    const node = nodes.get(comment.id);
    if (comment.parentId && nodes.has(comment.parentId)) {
      nodes.get(comment.parentId).replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return { roots, count: filtered.length };
};

const CommentForm = ({
  onSubmit,
  placeholder,
  submitLabel,
  isAuthenticated,
  onCancel
}) => {
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const captchaEnabled = !!config.hcaptchaSiteKey;

  const resetCaptcha = () => {
    try { captchaRef.current?.resetCaptcha(); } catch (e) {}
    setCaptchaToken('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) return;

    const trimmed = body.trim();
    if (!trimmed) {
      setError('Comment cannot be empty.');
      return;
    }
    if (trimmed.length > COMMENT_MAX_LENGTH) {
      setError(`Comment must be ${COMMENT_MAX_LENGTH} characters or less.`);
      return;
    }
    if (captchaEnabled && !captchaToken) {
      setError('Please complete the captcha.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      await onSubmit({ body: trimmed, captchaToken });
      setBody('');
      resetCaptcha();
    } catch (err) {
      setError(err?.message || 'Unable to post comment.');
      resetCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="comment-form" onSubmit={handleSubmit}>
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={placeholder}
        maxLength={COMMENT_MAX_LENGTH}
        rows={4}
        disabled={submitting}
      />
      {error ? <div className="alert alert-danger comment-error">{error}</div> : null}
      {config.hcaptchaSiteKey ? (
        <div className="comment-captcha">
          <HCaptcha
            sitekey={config.hcaptchaSiteKey}
            onVerify={(token) => setCaptchaToken(token || '')}
            onExpire={() => setCaptchaToken('')}
            ref={captchaRef}
          />
        </div>
      ) : null}
      <div className="comment-form-actions">
        <button type="submit" style={{marginBottom: '15px'}} className="btn btn-sm btn-outline-dark" disabled={submitting}>
          {submitting ? 'Sending…' : submitLabel}
        </button>
        {onCancel ? (
          <button type="button" style={{textDecoration: 'none'}} className="btn btn-sm btn-link" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
};

const GuideComments = ({
  scope = 'guide',
  stepPosition,
  stepId,
  comments = [],
  isAuthenticated,
  canModerate,
  currentUsername,
  projectSlug,
  guideSlug,
  onAuthNavigate,
  onCommentCreated,
  onCommentApproved,
  onCommentDeleted,
  autoOpen,
  openAllSignal,
  containerId
}) => {
  const normalizedScope = normalizeScope(scope);
  const [isOpen, setIsOpen] = useState(!!autoOpen);
  const [replyToId, setReplyToId] = useState(null);
  const [actionPendingId, setActionPendingId] = useState(null);
  const containerRef = useRef(null);

  const { roots, count } = useMemo(
    () => buildCommentTree(comments, normalizedScope, stepPosition, stepId),
    [comments, normalizedScope, stepPosition, stepId]
  );

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, [autoOpen]);

  useEffect(() => {
    if (openAllSignal > 0) {
      setIsOpen(true);
    }
  }, [openAllSignal]);

  const handleAuthClick = (mode) => {
    if (typeof onAuthNavigate === 'function') {
      onAuthNavigate(mode, normalizedScope, stepPosition);
    }
  };

  const submitComment = async ({ body, captchaToken, parentId }) => {
    if (!projectSlug || !guideSlug) {
      throw new Error('Guide not available.');
    }
    const payload = {
      body,
      scope: normalizedScope,
      ...(normalizedScope === 'step' ? { stepPosition, ...(stepId ? { stepId } : {}) } : {}),
      ...(parentId ? { parentId } : {}),
      ...(captchaToken ? { hcaptchaToken: captchaToken } : {})
    };
    const response = await api.post(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/comments`, payload);
    if (!response?.ok) {
      throw new Error(response?.message || 'Unable to post comment.');
    }
    if (response.comment && typeof onCommentCreated === 'function') {
      onCommentCreated(response.comment);
    }
    toast.success('Comment submitted and awaiting moderation.');
    setReplyToId(null);
  };

  const handleApprove = async (commentId) => {
    if (!commentId) return;
    setActionPendingId(commentId);
    try {
      const response = await api.post(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/comments/${commentId}/approve`);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to approve comment.');
      }
      if (typeof onCommentApproved === 'function') {
        if (Array.isArray(response.comments) && response.comments.length) {
          onCommentApproved(response.comments);
        } else if (response.comment) {
          onCommentApproved(response.comment);
        }
      }
      toast.success('Comment approved.');
    } catch (err) {
      toast.error(err?.message || 'Unable to approve comment.');
    } finally {
      setActionPendingId(null);
    }
  };

  const handleDelete = async (comment) => {
    const commentId = comment?.id;
    if (!commentId) return;
    const hasReplies = Array.isArray(comment?.replies) && comment.replies.length > 0;
    let confirmMessage = 'Delete this comment?';
    if (canModerate) {
      confirmMessage = 'Delete this comment and all replies? This cannot be undone.';
    } else if (hasReplies) {
      confirmMessage = 'Delete this comment? Replies will remain and your comment will show as deleted.';
    } else {
      confirmMessage = 'Delete this comment permanently?';
    }
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setActionPendingId(commentId);
    try {
      const response = await api.delete(`/api/v1/projects/${projectSlug}/guides/${guideSlug}/comments/${commentId}`);
      if (!response?.ok) {
        throw new Error(response?.message || 'Unable to delete comment.');
      }
      if (typeof onCommentDeleted === 'function') {
        onCommentDeleted(response);
      }
      if (response?.softDeleted) {
        toast.success('Comment deleted (replies kept).');
      } else {
        toast.success('Comment deleted.');
      }
    } catch (err) {
      toast.error(err?.message || 'Unable to delete comment.');
    } finally {
      setActionPendingId(null);
    }
  };

  const renderComment = (comment, depth = 0) => {
    const authorName = comment?.author?.name || 'User';
    const authorLabel = comment?.author?.isCreator ? ' (creator)' : '';
    const isPending = comment?.status === 'pending';
    const showApprove = canModerate && isPending;
    const isOwner = isAuthenticated && comment?.isOwner === true;
    const showDelete = canModerate || isOwner;
    const canReply = depth === 0;
    const replies = Array.isArray(comment.replies) ? comment.replies : [];
    const displayAuthor = capitalizeFirstLetter(authorName);
    const displayBody = capitalizeFirstLetter(comment?.body || '');

    return (
      <div
        key={comment.id}
        className={`comment-item${isPending ? ' pending-comment' : ''}`}
        data-comment-id={comment.id}
        data-comment-status={isPending ? 'pending' : 'approved'}
        style={{ marginLeft: depth ? depth * 18 : 0 }}
      >
        <div className="comment-meta">
          <span className="comment-author">{displayAuthor}{authorLabel}</span>
          <span className="comment-time">{moment(comment.createdAt).fromNow()}</span>
          {isPending ? <span className="badge text-bg-warning">Awaiting moderation</span> : null}
        </div>
        <div className="comment-body">{displayBody}</div>
        <div className="comment-actions">
          {canReply ? (
            <button
              type="button"
              className="btn btn-link p-0"
              style={{textDecoration: 'none'}}
              onClick={() => {
                if (!isAuthenticated) {
                  handleAuthClick('login');
                  return;
                }
                setReplyToId((prev) => (prev === comment.id ? null : comment.id));
              }}
            >
              Reply
            </button>
          ) : null}
          {showApprove ? (
            <button
              type="button"
              className="btn btn-link p-0"
              style={{textDecoration: 'none'}}
              onClick={() => handleApprove(comment.id)}
              disabled={actionPendingId === comment.id}
            >
              Approve
            </button>
          ) : null}
          {showDelete ? (
            <button
              type="button"
              className="btn btn-link text-danger p-0"
              onClick={() => handleDelete(comment)}
              style={{textDecoration: 'none'}}
              disabled={actionPendingId === comment.id}
            >
              Delete
            </button>
          ) : null}
        </div>
        {replyToId === comment.id ? (
          isAuthenticated ? (
            <CommentForm
              isAuthenticated={isAuthenticated}
              placeholder="Write a reply…"
              submitLabel="Post reply"
              onSubmit={({ body, captchaToken }) => submitComment({ body, captchaToken, parentId: comment.id })}
              onCancel={() => setReplyToId(null)}
            />
          ) : null
        ) : null}
        {replies.length ? replies.map((reply) => renderComment(reply, depth + 1)) : null}
      </div>
    );
  };

  return (
    <div className="comment-section" ref={containerRef} id={containerId}>
      <button
        type="button"
        className="btn btn-link comment-toggle"
        style={{textDecoration: 'none'}}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        Comments ({count})
      </button>
      {isOpen ? (
        <div className="comment-panel">
          {count === 0 ? <div className="comment-empty">No comments yet.</div> : null}
          <div className="comment-list">
            {roots.map((comment) => renderComment(comment))}
          </div>
          {replyToId ? null : (
            <div className="comment-new">
              {isAuthenticated ? (
                <CommentForm
                  isAuthenticated={isAuthenticated}
                  placeholder="Leave a comment…"
                  submitLabel="Post comment"
                  onSubmit={({ body, captchaToken }) => submitComment({ body, captchaToken })}
                />
              ) : (
                <div className="comment-login">
                  <button style={{textDecoration: "none", fontStyle: "italic", marginBottom: 4}} type="button" className="btn btn-link p-0" onClick={() => handleAuthClick('login')}>
                    Log in
                  </button>
                  {' or '}
                  <button style={{textDecoration: "none", fontStyle: "italic", marginBottom: 4}} type="button" className="btn btn-link p-0" onClick={() => handleAuthClick('register')}>
                    Register
                  </button>
                  {' '}to leave a comment...
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default GuideComments;
